import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { activateSubscription } from '../../services/subscriptionService';

/**
 * 🔑 Página de Activación Manual de Suscripciones
 * 
 * Permite al admin activar suscripciones después de verificar el pago
 */
const ActivateSubscription = () => {
  const [userId, setUserId] = useState('');
  const [paypalOrderId, setPaypalOrderId] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleActivate = async (e) => {
    e.preventDefault();
    
    if (!userId.trim()) {
      setMessage({ type: 'error', text: 'Por favor ingresa el User ID' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      await activateSubscription(
        userId.trim(),
        paypalOrderId.trim() || `MANUAL-${Date.now()}`,
        'manual-activation'
      );
      
      setMessage({ 
        type: 'success', 
        text: '✅ Suscripción activada exitosamente por 30 días' 
      });
      
      // Limpiar formulario
      setUserId('');
      setPaypalOrderId('');
    } catch (error) {
      console.error('Error activando suscripción:', error);
      setMessage({ 
        type: 'error', 
        text: '❌ Error al activar suscripción: ' + error.message 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f172a',
      padding: '2rem',
      color: '#f1f5f9'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Navigation */}
        <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem' }}>
          <Link
            to="/admin/dashboard"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '0.5rem',
              color: '#3b82f6',
              textDecoration: 'none',
              transition: 'all 0.3s ease'
            }}
          >
            ← Admin Dashboard
          </Link>
        </div>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔑</div>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Activar Suscripción
          </h1>
          <p style={{ color: '#94a3b8', fontSize: '1.1rem' }}>
            Activa manualmente suscripciones después de verificar el pago
          </p>
        </div>

        {/* Form */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          padding: '2rem'
        }}>
          <form onSubmit={handleActivate}>
            {/* User ID */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#cbd5e1',
                fontWeight: 'bold'
              }}>
                User ID (requerido)
              </label>
              <input
                type="text"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="Ej: abc123def456..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  borderRadius: '0.5rem',
                  color: '#f1f5f9',
                  fontSize: '1rem'
                }}
                required
              />
              <p style={{ 
                color: '#64748b', 
                fontSize: '0.85rem', 
                marginTop: '0.5rem' 
              }}>
                El usuario debe enviarte este ID junto con el comprobante de pago
              </p>
            </div>

            {/* PayPal Order ID (opcional) */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#cbd5e1',
                fontWeight: 'bold'
              }}>
                PayPal Order ID (opcional)
              </label>
              <input
                type="text"
                value={paypalOrderId}
                onChange={(e) => setPaypalOrderId(e.target.value)}
                placeholder="Ej: 8XY12345AB678901C"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  borderRadius: '0.5rem',
                  color: '#f1f5f9',
                  fontSize: '1rem'
                }}
              />
              <p style={{ 
                color: '#64748b', 
                fontSize: '0.85rem', 
                marginTop: '0.5rem' 
              }}>
                ID de la transacción de PayPal (si está disponible)
              </p>
            </div>

            {/* Message */}
            {message && (
              <div style={{
                padding: '1rem',
                borderRadius: '0.5rem',
                marginBottom: '1.5rem',
                background: message.type === 'success' 
                  ? 'rgba(16, 185, 129, 0.1)' 
                  : 'rgba(239, 68, 68, 0.1)',
                border: `1px solid ${message.type === 'success' 
                  ? 'rgba(16, 185, 129, 0.3)' 
                  : 'rgba(239, 68, 68, 0.3)'}`,
                color: message.type === 'success' ? '#10b981' : '#ef4444'
              }}>
                {message.text}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '1rem',
                background: loading 
                  ? 'rgba(100, 116, 139, 0.5)' 
                  : 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              {loading ? '⏳ Activando...' : '✅ Activar Suscripción (30 días)'}
            </button>
          </form>

          {/* Info Box */}
          <div style={{
            marginTop: '2rem',
            padding: '1.5rem',
            background: 'rgba(59, 130, 246, 0.1)',
            borderRadius: '0.5rem',
            border: '1px solid rgba(59, 130, 246, 0.3)'
          }}>
            <h3 style={{ 
              color: '#3b82f6', 
              marginBottom: '1rem',
              fontSize: '1.1rem'
            }}>
              📋 Proceso de Activación
            </h3>
            <ol style={{ 
              color: '#94a3b8', 
              fontSize: '0.9rem',
              paddingLeft: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              <li>Usuario realiza el pago de $2 USD a: ceed.educacion@gmail.com</li>
              <li>Usuario envía comprobante + User ID al correo</li>
              <li>Verificas el pago en tu cuenta de PayPal</li>
              <li>Ingresas el User ID aquí y activas la suscripción</li>
              <li>Usuario obtiene acceso inmediato por 30 días</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivateSubscription;
